import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { productsUrl, warehouseProductUrl } from '@/utils/productLinks';
import { useAuth } from '@/contexts/AuthContext';
import { apiService } from '@/services/api';
import { countryTaxRateFor } from '@/utils/countries';
import { DrinkOut, DrinkCreate, DrinkTypeOut, EventOut, EventVenueOut, BulkAssignVenueRequest, ProductOut, BulkImportResult as BulkImportResultData } from '@/types/api';
import { getProductImageProps } from '@/utils/imageUtils';
import { canAccessAllEvents, isSuperAdmin, isOrganizer } from '@/utils/roleUtils';
import Card from '@/components/atoms/Card';
import Button from '@/components/atoms/Button';
import CartaProductPicker from '@/components/molecules/CartaProductPicker/CartaProductPicker';
import BulkImportResult from '@/components/molecules/BulkImportResult/BulkImportResult';
import { FormField } from '@/components/molecules/FormField';
import { SearchableSelect } from '@/components/molecules/SearchableSelect';
import Pagination from '@/components/molecules/Pagination/Pagination';
import { TranslationFields, TranslationFieldsHandle } from '@/components/molecules/TranslationFields';
import './DrinkManagement.scss';
import { useCurrency } from '@/contexts/CurrencyContext';
import { usePriceFormat } from '@/hooks/usePriceFormat';

interface DrinkFormData {
  name: string;
  price: number;
  tax_rate: number;
  image_url: string;
  description: string;
  event_id: number;
  event_venue_id: number | null;
  product_id: number | null;
  type_id: number;
  stock_quantity: number;
  max_stock: number;
  min_stock_threshold: number;
  is_available: boolean;
}

const initialFormData: DrinkFormData = {
  name: '',
  price: 0,
  tax_rate: 0,
  image_url: '',
  description: '',
  event_id: 0,
  event_venue_id: null,
  product_id: null,
  type_id: 0,
  stock_quantity: 0,
  max_stock: 100,
  min_stock_threshold: 10,
  is_available: true
};

// Carta rows per page in the table
const PAGE_SIZE = 25;

const DrinkManagement: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { currency } = useCurrency();
  const { format } = usePriceFormat();
  const [drinks, setDrinks] = useState<DrinkOut[]>([]);
  const [drinkTypes, setDrinkTypes] = useState<DrinkTypeOut[]>([]);
  const [events, setEvents] = useState<EventOut[]>([]);
  const [venues, setVenues] = useState<Record<number, EventVenueOut[]>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingDrink, setEditingDrink] = useState<DrinkOut | null>(null);
  const translationsRef = useRef<TranslationFieldsHandle>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState<DrinkFormData>(initialFormData);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [selectedEvent, setSelectedEvent] = useState<number | null>(null);
  const [selectedVenue, setSelectedVenue] = useState<number | null>(null);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [showBulkImport, setShowBulkImport] = useState(false);
  const [bulkImportFile, setBulkImportFile] = useState<File | null>(null);
  const [isBulkImporting, setIsBulkImporting] = useState(false);
  const [bulkImportEventId, setBulkImportEventId] = useState<number | null>(null);
  const [priceInput, setPriceInput] = useState<string>('');
  const [isTaxIncluded, setIsTaxIncluded] = useState<boolean>(false);
  const [taxInput, setTaxInput] = useState<string>('');
  const [countryTaxRates, setCountryTaxRates] = useState<Record<string, number>>({});
  const [selectedDrinkIds, setSelectedDrinkIds] = useState<Set<number>>(new Set());
  const [page, setPage] = useState(1);
  const [bulkTargetEventId, setBulkTargetEventId] = useState<number | null>(null);
  const [bulkTargetVenueId, setBulkTargetVenueId] = useState<number | null>(null);
  const [isBulkAssigning, setIsBulkAssigning] = useState(false);
  const [localProducts, setLocalProducts] = useState<ProductOut[]>([]);
  const [localProductsLoaded, setLocalProductsLoaded] = useState(false);
  const [bulkImportResult, setBulkImportResult] = useState<BulkImportResultData | null>(null);
  const navigate = useNavigate();
  const [showCartaExplainer, setShowCartaExplainer] = useState(() => {
    try {
      return localStorage.getItem('carta-explainer-dismissed') !== '1';
    } catch {
      return true;
    }
  });

  const dismissCartaExplainer = () => {
    setShowCartaExplainer(false);
    try {
      localStorage.setItem('carta-explainer-dismissed', '1');
    } catch {
      // ignore - per-viewer convenience only
    }
  };

  // Catalog products of the selected event's Local: every carta row is
  // created from one of them (empty if the event has no Local).
  useEffect(() => {
    const event = events.find((e) => e.id === formData.event_id);
    if (!event?.local_id) {
      setLocalProducts([]);
      setLocalProductsLoaded(true);
      return;
    }
    setLocalProductsLoaded(false);
    apiService.getProducts(event.local_id)
      .then((data) => {
        const products = data.filter((p) => p.is_drink);
        setLocalProducts(products);
        // A product of another Local can't stay selected after changing event
        setFormData((prev) => (prev.product_id && !products.some((p) => p.id === prev.product_id)
          ? { ...prev, product_id: null }
          : prev));
      })
      .catch(() => setLocalProducts([]))
      .finally(() => setLocalProductsLoaded(true));
  }, [formData.event_id, events]);

  // Fetch venues for a specific event
  const fetchVenues = useCallback(async (eventId: number) => {
    try {
      const venuesData = await apiService.getEventVenues(eventId);
      setVenues(prev => ({ ...prev, [eventId]: venuesData }));
    } catch (err: any) {
      console.error(`Error fetching venues for event ${eventId}:`, err);
    }
  }, []);

  // Fetch country tax rates
  const fetchCountryTaxRates = useCallback(async () => {
    try {
      const response = await apiService.getAllCountryTaxRates();
      setCountryTaxRates(response.country_drink_tax_rates || {});
    } catch (err: any) {
      console.error('Error fetching country tax rates:', err);
    }
  }, []);

  // Fetch data for the current organizer or all events for super admin
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Fetch events first - use getEvents() for super admin, getMyManagedEvents() for organizer
      const eventsData = canAccessAllEvents(user)
        ? await apiService.getEvents()
        : await apiService.getMyManagedEvents();
      setEvents(eventsData);
      
      // Fetch drink types
      const typesData = await apiService.getDrinkTypes();
      setDrinkTypes(typesData);
      
      // Fetch country tax rates
      await fetchCountryTaxRates();
      
      // Fetch venues for all events
      for (const event of eventsData) {
        await fetchVenues(event.id);
      }
      
      // Fetch drinks for selected event or all organizer's events
      if (selectedEvent) {
        // Fetch drinks for specific event, optionally narrowed to a venue
        const drinksData = await apiService.getAllDrinks({
          event_id: selectedEvent,
          ...(selectedVenue ? { event_venue_id: selectedVenue } : {})
        });
        setDrinks(drinksData);
      } else {
        // Fetch drinks for all organizer's events
        const allDrinks: DrinkOut[] = [];
        for (const event of eventsData) {
          const eventDrinks = await apiService.getAllDrinks({
            event_id: event.id
          });
          allDrinks.push(...eventDrinks);
        }
        setDrinks(allDrinks);
      }
    } catch (err: any) {
      setError(err.message || t('errors.general'));
    } finally {
      setLoading(false);
    }
  }, [selectedEvent, selectedVenue, t, user, fetchVenues, fetchCountryTaxRates]);

  const handleInputChange = (field: keyof DrinkFormData, value: string | number | boolean | null) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  // The carta row shows its Product's catalog details (name, description,
  // photo, type): they're copied from the Product and are read-only here.
  const handleProductChange = (productId: number | null) => {
    const product = localProducts.find((p) => p.id === productId);
    setFormData(prev => ({
      ...prev,
      product_id: productId,
      name: product?.name ?? prev.name,
      description: product?.description ?? '',
      image_url: product?.image_url ?? '',
      type_id: product?.type_id ?? prev.type_id,
    }));
    setFormErrors(prev => ({ ...prev, product_id: '', type_id: '' }));
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!formData.product_id) {
      errors.product_id = t('carta.productPicker.required');
    } else if (!formData.type_id) {
      errors.product_id = t('carta.productPicker.productNeedsType');
    }

    if (formData.price <= 0) {
      errors.price = t('errors.validation.positiveNumber');
    }

    if (formData.event_id === 0) {
      errors.event_id = t('errors.validation.required');
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      // The first error can be far above the submit button (e.g. the product
      // picker): bring it into view so the click doesn't look like it did nothing
      setTimeout(() => {
        document.querySelector('.drink-management-page__form .form-field__error')
          ?.parentElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 0);
      return;
    }

    try {
      setIsSubmitting(true);
      
      const rate = parseFloat(taxInput || '0') || 0;
      const entered = parseFloat(priceInput || '0') || 0;
      const roundedEntered = Number(entered.toFixed(2));
      const basePrice = isTaxIncluded
        ? Number((rate > 0 ? roundedEntered / (1 + rate / 100) : roundedEntered).toFixed(2))
        : roundedEntered;

      const drinkData: DrinkCreate = {
        name: formData.name,
        price: basePrice,
        tax_rate: rate,
        image_url: formData.image_url || null, // the Product's photo (read-only here)
        description: formData.description || null,
        event_id: formData.event_id,
        event_venue_id: formData.event_venue_id,
        product_id: formData.product_id,
        type_id: formData.type_id,
        is_available: formData.is_available
      };

      if (editingDrink) {
        await apiService.updateDrink(editingDrink.id, { ...drinkData, name: formData.name, type_id: formData.type_id });
        await translationsRef.current?.save();
      } else {
        await apiService.createDrink(drinkData);
      }

      // Reset form
      setFormData(initialFormData);
      setShowCreateForm(false);
      setEditingDrink(null);
      setImageFile(null);
      
      // Refresh data
      await fetchData();
      
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || t('errors.general'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const openCreateForm = () => {
    setEditingDrink(null);
    setFormData({
      ...initialFormData,
      event_id: selectedEvent ?? 0,
      event_venue_id: selectedEvent ? selectedVenue : null,
    });
    if (selectedEvent) fetchVenues(selectedEvent);
    setFormErrors({});
    setShowCreateForm(true);
  };

  const handleEdit = (drink: DrinkOut) => {
    setEditingDrink(drink);
    setFormData({
      name: drink.name,
      price: drink.price,
      tax_rate: drink.tax_rate,
      image_url: drink.image_url || '',
      description: drink.description || '',
      event_id: drink.event_id,
      event_venue_id: drink.event_venue_id || null,
      product_id: drink.product_id || null,
      type_id: drink.type_id,
      stock_quantity: drink.stock_quantity,
      max_stock: drink.max_stock,
      min_stock_threshold: drink.min_stock_threshold,
      is_available: drink.is_available
    });
    setPriceInput(drink.price ? String(drink.price) : '');
    setTaxInput(drink.tax_rate ? String(drink.tax_rate) : '');
    setIsTaxIncluded(!drink.tax_rate || Number(drink.tax_rate) === 0);
    setImageFile(null);
    setShowCreateForm(true);
  };

  // Soft-deletes the selected carta rows (the catalog Product and its stock stay
  // in Productos). Each row goes through DELETE /drinks/{id} so the same
  // per-row permission checks apply; failures are reported, not hidden.
  const handleBulkDelete = async () => {
    const ids = Array.from(selectedDrinkIds);
    if (ids.length === 0) return;
    if (!window.confirm(t('management.drinks.bulkDelete.confirm', { count: ids.length }))) return;

    try {
      setIsBulkAssigning(true);
      const results = await Promise.allSettled(ids.map((id) => apiService.deleteDrink(id)));
      const failed = results.filter((r) => r.status === 'rejected').length;
      if (failed > 0) {
        setError(t('management.drinks.bulkDelete.partial', { deleted: ids.length - failed, failed }));
      } else {
        alert(t('management.drinks.bulkDelete.success', { count: ids.length }));
      }
      clearSelection();
      await fetchData();
    } finally {
      setIsBulkAssigning(false);
    }
  };

  const handleToggleAvailability = async (drinkId: number, currentAvailability: boolean) => {
    try {
      await apiService.updateDrinkAvailability(drinkId, !currentAvailability);
      await fetchData();
    } catch (err: any) {
      setError(err.message || t('errors.general'));
    }
  };

  const handleImageChange = (file: File | null, previewUrl: string | null) => {
    setImageFile(file);
    setFormData(prev => ({ ...prev, image_url: previewUrl || '' }));
    setFormErrors(prev => ({ ...prev, image_url: '' }));
  };

  const handleCancel = () => {
    setFormData(initialFormData);
    setPriceInput('');
    setTaxInput('');
    setIsTaxIncluded(false);
    setShowCreateForm(false);
    setEditingDrink(null);
    setImageFile(null);
    setFormErrors({});
  };
  const toggleDrinkSelection = (drinkId: number) => {
    setSelectedDrinkIds(prev => {
      const next = new Set(prev);
      if (next.has(drinkId)) {
        next.delete(drinkId);
      } else {
        next.add(drinkId);
      }
      return next;
    });
  };

  const clearSelection = () => {
    setSelectedDrinkIds(new Set());
    setBulkTargetEventId(null);
    setBulkTargetVenueId(null);
  };

  const handleBulkAssign = async () => {
    if (selectedDrinkIds.size === 0 || !bulkTargetEventId) return;

    try {
      setIsBulkAssigning(true);
      const payload: BulkAssignVenueRequest = {
        item_ids: Array.from(selectedDrinkIds),
        event_id: bulkTargetEventId,
        event_venue_id: bulkTargetVenueId
      };
      const result = await apiService.bulkAssignDrinksToVenue(payload);
      alert(t('management.drinks.bulkAssign.success', {
        count: result.updated_count,
        defaultValue: `${result.updated_count} tragos actualizados`
      }));
      clearSelection();
      await fetchData();
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || t('errors.general'));
    } finally {
      setIsBulkAssigning(false);
    }
  };



  const handleTaxRateChange = (sanitizedValue: string) => {
    let next = sanitizedValue;
    let numeric = parseFloat(next);
    if (!isNaN(numeric) && numeric > 99) {
      next = '99';
      numeric = 99;
    }
    setTaxInput(next);
    handleInputChange('tax_rate', isNaN(numeric) ? 0 : numeric);
  };

  const handlePriceInputChange = (sanitizedValue: string) => {
    setPriceInput(sanitizedValue);
    const numeric = parseFloat(sanitizedValue);
    handleInputChange('price', isNaN(numeric) ? 0 : numeric);
  };

  // The carta template lists the event Local's products and the event's
  // bars, so it's downloaded for the event picked in the import panel.
  const handleDownloadTemplate = async () => {
    if (!bulkImportEventId) {
      setError(t('management.drinks.bulkImport.selectEvent'));
      return;
    }
    try {
      const blob = await apiService.downloadDrinkTemplate(bulkImportEventId);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'drinks_carta_template.xlsx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      setError(err.message || t('management.drinks.bulkImport.error'));
    }
  };

  const handleBulkImportFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const allowedTypes = [
        'text/csv',
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      ];
      
      if (allowedTypes.includes(file.type) || file.name.endsWith('.csv') || file.name.endsWith('.xlsx') || file.name.endsWith('.xls')) {
        setBulkImportFile(file);
        setError(null);
      } else {
        setError(t('management.drinks.bulkImport.invalidFileType'));
      }
    }
  };

  const handleShowBulkImport = () => {
    setBulkImportEventId(selectedEvent); // Pre-select the filtered event
    setBulkImportResult(null);
    setShowBulkImport(true);
  };

  const handleBulkImport = async () => {
    if (!bulkImportFile) {
      setError(t('management.drinks.bulkImport.noFileSelected'));
      return;
    }

    if (!bulkImportEventId) {
      setError(t('management.drinks.bulkImport.selectEvent'));
      return;
    }

    try {
      setIsBulkImporting(true);
      setError(null);
      
      const result = await apiService.bulkImportDrinks(bulkImportFile, bulkImportEventId);

      // Keep the panel open with the per-row result (errors included)
      setBulkImportResult(result);
      setBulkImportFile(null);

      // Refresh data
      await fetchData();
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || t('management.drinks.bulkImport.error'));
    } finally {
      setIsBulkImporting(false);
    }
  };

  const handleCancelBulkImport = () => {
    setBulkImportFile(null);
    setBulkImportEventId(null);
    setBulkImportResult(null);
    setShowBulkImport(false);
    setError(null);
  };

  const getStockStatus = (drink: DrinkOut) => {
    if (drink.stock_quantity <= 0) return 'out-of-stock';
    if (drink.stock_quantity <= drink.min_stock_threshold) return 'low-stock';
    return 'in-stock';
  };

  const getStockStatusText = (drink: DrinkOut) => {
    const status = getStockStatus(drink);
    switch (status) {
      case 'out-of-stock':
        return t('management.drinks.outOfStock');
      case 'low-stock':
        return t('management.drinks.lowStock');
      default:
        return t('management.drinks.inStock');
    }
  };

  const getEventName = (eventId: number) => events.find(e => e.id === eventId)?.name || '—';

  const getVenueName = (eventId: number, venueId: number | null | undefined) => {
    if (!venueId) return t('management.drinks.pista', { defaultValue: 'Pista' });
    const venue = venues[eventId]?.find(v => v.id === venueId);
    if (!venue) return '—';
    return venue.venue_type_name ? `${venue.name} (${venue.venue_type_name})` : venue.name;
  };


  useEffect(() => {
    fetchData();
  }, [fetchData]);
  
  // Set tax rate from global settings when event is selected or form opens
  useEffect(() => {
    if (showCreateForm && !editingDrink && formData.event_id > 0) {
      const selectedEventData = events.find(e => e.id === formData.event_id);
      if (selectedEventData?.country) {
        const taxRate = countryTaxRateFor(countryTaxRates, selectedEventData.country);
        if (taxRate > 0) {
          handleInputChange('tax_rate', taxRate);
          setTaxInput(String(taxRate));
          setIsTaxIncluded(false);
        }
      }
    }
  }, [showCreateForm, formData.event_id, events, countryTaxRates, editingDrink]);

  // Keep price input string in sync when opening a fresh form
  useEffect(() => {
    if (showCreateForm && !editingDrink) {
      setPriceInput(formData.price > 0 ? String(formData.price) : '');
      if (formData.tax_rate > 0) {
        setTaxInput(String(formData.tax_rate));
        setIsTaxIncluded(false);
      } else {
        setTaxInput('');
        setIsTaxIncluded(true);
      }
    }
  }, [showCreateForm, editingDrink, formData.price, formData.tax_rate]);

  // Keep isTaxIncluded in sync with numeric tax value
  useEffect(() => {
    if (!showCreateForm) return;
    const parsed = parseFloat(taxInput);
    setIsTaxIncluded(taxInput === '' || isNaN(parsed) || parsed === 0);
  }, [taxInput, showCreateForm]);

  useEffect(() => {
    if (bulkTargetEventId && !venues[bulkTargetEventId]) {
      fetchVenues(bulkTargetEventId);
    }
    setBulkTargetVenueId(null); // reset venue when target event changes
  }, [bulkTargetEventId, venues, fetchVenues]);

  if (!canAccessAllEvents(user)) {
    return (
      <div className="drink-management-page">
        <div className="container">
          <Card variant="elevated" padding="lg">
            <h1>{t('errors.unauthorized')}</h1>
            <p>{t('management.drinks.organizerOnly')}</p>
          </Card>
        </div>
      </div>
    );
  }

  // Client-side pages over the loaded rows; back to page 1 when the filters change
  const totalPages = Math.max(1, Math.ceil(drinks.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageDrinks = drinks.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  return (
    <div className="drink-management-page">
      <div className="container">
        <div className="drink-management-page__header">
          <h1 className="drink-management-page__title">
            {t('management.drinks.title')}
          </h1>
          <p className="drink-management-page__description">
            {t('management.drinks.description')}
          </p>
        </div>

        {showCartaExplainer && (
          <Card variant="elevated" padding="md" className="drink-management-page__explainer">
            <div className="drink-management-page__explainer-header">
              <h3>{t('carta.explainer.title')}</h3>
              <Button variant="ghost" size="sm" onClick={dismissCartaExplainer}>
                {t('carta.explainer.dismiss')}
              </Button>
            </div>
            <p>{t('carta.explainer.intro')}</p>
            <Button
              variant="outline"
              size="sm"
              className="drink-management-page__explainer-link"
              onClick={() => navigate('/products')}
            >
              {t('carta.explainer.goToProducts')}
            </Button>
          </Card>
        )}

        {error && (
          <Card variant="elevated" padding="md" className="drink-management-page__error">
            <p>{error}</p>
          </Card>
        )}

        {/* Event Filter */}
        <Card variant="elevated" padding="lg" className="drink-management-page__filter">
          <div className="drink-management-page__filter-content">
            <SearchableSelect<number>
              name="event_filter"
              label={t('management.drinks.filterByEvent')}
              placeholder={t('management.drinks.allEvents')}
              clearable
              options={events.map((event) => ({ value: event.id, label: event.name }))}
              value={selectedEvent || null}
              onChange={(value) => {
                setSelectedEvent(value);
                setSelectedVenue(null);
                setPage(1);
              }}
            />

            <SearchableSelect<number>
              name="venue_filter"
              label={t('management.drinks.filterByVenue')}
              placeholder={t('management.drinks.allVenues')}
              clearable
              options={(selectedEvent ? venues[selectedEvent] || [] : []).map((venue) => ({ value: venue.id, label: venue.name }))}
              value={selectedVenue || null}
              onChange={(value) => {
                setSelectedVenue(value);
                setPage(1);
              }}
              disabled={!selectedEvent}
            />

            <div className="drink-management-page__action-buttons">
              <Button
                variant="primary"
                onClick={openCreateForm}
                className="drink-management-page__create-btn"
              >
                {t('management.drinks.addDrink')}
              </Button>

              <Button
                variant="outline"
                onClick={() => navigate(productsUrl(events.find((ev) => ev.id === selectedEvent)?.local_id, 'drink'))}
              >
                {t('carta.createProduct')}
              </Button>
              
              <Button
                variant="ghost"
                onClick={handleShowBulkImport}
                className="drink-management-page__bulk-import-btn"
              >
                {t('management.drinks.bulkImport.uploadFile')}
              </Button>
            </div>
          </div>
        </Card>

        {/* Create/Edit Drink Form */}
        {showCreateForm && (
          <Card variant="elevated" padding="lg" className="drink-management-page__form">
            <h2>{editingDrink ? t('management.drinks.editDrink') : t('management.drinks.addDrink')}</h2>
            
            <form onSubmit={handleSubmit}>
              <div className="drink-management-page__form-grid">
                <SearchableSelect<number>
                  name="event_id"
                  label={t('management.drinks.event')}
                  placeholder={t('management.drinks.selectEvent')}
                  error={formErrors.event_id}
                  required
                  options={events.map((event) => ({ value: event.id, label: event.name }))}
                  value={formData.event_id || null}
                  onChange={(value) => {
                    const eventId = value || 0;
                    handleInputChange('event_id', eventId);
                    // Reset venue selection when event changes
                    handleInputChange('event_venue_id', null);
                    // Fetch venues for the selected event
                    if (eventId > 0) {
                      fetchVenues(eventId);
                      // Set tax rate from global settings based on event's country
                      const selectedEventData = events.find(e => e.id === eventId);
                      if (selectedEventData?.country && !editingDrink) {
                        const taxRate = countryTaxRateFor(countryTaxRates, selectedEventData.country);
                        if (taxRate > 0) {
                          handleInputChange('tax_rate', taxRate);
                          setTaxInput(String(taxRate));
                          setIsTaxIncluded(false);
                        }
                      }
                    }
                  }}
                  disabled={isSubmitting}
                />

                {editingDrink && !editingDrink.product_id && (
                  <div className="drink-management-page__unlinked-warning form-field--full-width" role="alert">
                    <strong>{t('carta.unlinked.title')}</strong>
                    <p>{t('carta.unlinked.text')}</p>
                  </div>
                )}

                <CartaProductPicker
                  event={events.find((ev) => ev.id === formData.event_id)}
                  products={localProducts}
                  productsLoaded={localProductsLoaded}
                  value={formData.product_id}
                  onChange={handleProductChange}
                  kind="drink"
                  typeName={drinkTypes.find((type) => type.id === formData.type_id)?.name}
                  error={formErrors.product_id}
                  disabled={isSubmitting}
                />

                <SearchableSelect<number>
                  name="event_venue_id"
                  label={t('management.drinks.venue')}
                  placeholder={t('management.drinks.selectVenue')}
                  clearable
                  error={formErrors.event_venue_id}
                  options={(venues[formData.event_id] || []).map((venue) => ({ value: venue.id, label: venue.name }))}
                  value={formData.event_venue_id || null}
                  onChange={(value) => handleInputChange('event_venue_id', value)}
                  disabled={isSubmitting}
                />

                <FormField
                  type="price"
                  name="price"
                  label={t('management.drinks.price')}
                  error={formErrors.price}
                  required
                  value={priceInput}
                  onChange={(e) => handlePriceInputChange(e.target.value)}
                  placeholder="0.00"
                  disabled={isSubmitting}
                  prefix={currency.symbol || '$'}
                />


                <FormField
                  type="price"
                  name="tax_rate"
                  label={t('management.drinks.taxRate')}
                  error={formErrors.tax_rate}
                  value={taxInput}
                  onChange={(e) => handleTaxRateChange(e.target.value)}
                  placeholder="0.0"
                  disabled={isSubmitting || !isSuperAdmin(user)}
                  suffix="%"
                  helperText={!isSuperAdmin(user) ? t('management.drinks.taxRateFromGlobalSettings') : undefined}
                />
                

                

                {(isSuperAdmin(user) || isOrganizer(user)) && (
                  <div className="form-field form-field--full-width">
                    <label className="form-field__label">
                      {t('management.drinks.taxRateIncludedQuestion')}
                    </label>
                    <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', width: '50%' }}>
                        <input
                          className="drink-management-page__tax-included-radio"
                          type="radio"
                          name="tax_included_drink"
                          checked={isTaxIncluded}
                          onChange={() => {
                            // Only affect preview; do not mutate inputs
                            setIsTaxIncluded(true);
                          }}
                          disabled={isSubmitting}
                        />
                        <span>{t('management.drinks.taxRateIncluded')}</span>
                      </label>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', width: '50%' }}>
                        <input
                          className="drink-management-page__tax-included-radio"
                          type="radio"
                          name="tax_included_drink"
                          checked={!isTaxIncluded}
                          onChange={() => {
                            // Only affect preview; do not mutate inputs
                            setIsTaxIncluded(false);
                          }}
                          disabled={isSubmitting}
                        />
                        <span>
                        {t('management.drinks.taxRateNotIncluded')}
                        </span>
                      </label>
                    </div>
                  </div>
                )}

                {/* Auto-calculation preview */}
                <div className="form-field form-field--full-width drink-management-page__price-preview">
                  {(() => {
                    const rate = parseFloat(taxInput || '0') || 0;
                    if (isTaxIncluded) {
                      const finalPrice = parseFloat(priceInput || '0') || 0;
                      const base = rate > 0 ? finalPrice / (1 + rate / 100) : finalPrice;
                      const taxAmount = finalPrice - base;
                      return (
                        <>
                          <div className="price-preview__row">
                            <span className="price-preview__label">{t('management.common.basePrice', 'Precio base')}:</span>
                            <span className="price-preview__value">{format(base)}</span>
                          </div>
                          {rate > 0 && (
                            <div className="price-preview__row">
                              <span className="price-preview__label">{t('management.common.tax', 'IVA')} ({rate.toFixed(1)}%):</span>
                              <span className="price-preview__value">{format(taxAmount)}</span>
                            </div>
                          )}
                          <div className="price-preview__row price-preview__row--total">
                            <span className="price-preview__label">{t('management.common.finalPrice', 'Precio final')}:</span>
                            <span className="price-preview__value">{format(finalPrice)}</span>
                          </div>
                        </>
                      );
                    }
                    const base = parseFloat(priceInput || '0') || 0;
                    const taxAmount = base * (rate / 100);
                    const finalPrice = base + taxAmount;
                    return (
                      <>
                        <div className="price-preview__row">
                          <span className="price-preview__label">{t('management.common.basePrice', 'Precio base')}:</span>
                          <span className="price-preview__value">{format(base)}</span>
                        </div>
                        {rate > 0 && (
                          <div className="price-preview__row">
                            <span className="price-preview__label">{t('management.common.tax', 'IVA')} ({rate.toFixed(1)}%):</span>
                            <span className="price-preview__value">{format(taxAmount)}</span>
                          </div>
                        )}
                        <div className="price-preview__row price-preview__row--total">
                          <span className="price-preview__label">{t('management.common.finalPrice', 'Precio final')}:</span>
                          <span className="price-preview__value">{format(finalPrice)}</span>
                        </div>
                      </>
                    );
                  })()}
                </div>

                <div className="drink-management-page__product-stock-note form-field--full-width">
                  <p>{t('carta.stockManagedInProducts')}</p>
                  {formData.product_id && events.find((ev) => ev.id === formData.event_id)?.local_id && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => navigate(warehouseProductUrl(events.find((ev) => ev.id === formData.event_id)?.local_id!, formData.product_id!))}
                    >
                      {t('carta.viewStock')}
                    </Button>
                  )}
                </div>

                <FormField
                  type="checkbox"
                  name="is_available"
                  label={t('management.drinks.available')}
                  error={formErrors.is_available}
                  checked={formData.is_available}
                  onChange={(e) => handleInputChange('is_available', (e.target as HTMLInputElement).checked)}
                  disabled={isSubmitting}
                />
              </div>

              {editingDrink && (
                <TranslationFields
                  ref={translationsRef}
                  entityType="drink"
                  entityId={editingDrink.id}
                  disabled={isSubmitting}
                />
              )}

              <div className="drink-management-page__form-actions">
                
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCancel}
                  disabled={isSubmitting}
                >
                  {t('common.cancel')}
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? t('common.loading') : (editingDrink ? t('common.update') : t('common.create'))}
                </Button>
              </div>
            </form>
          </Card>
        )}

        {/* Bulk Import Form */}
        {showBulkImport && (
          <Card variant="elevated" padding="lg" className="drink-management-page__bulk-import-form">
            <h2>{t('management.drinks.bulkImport.title')}</h2>
            <p className="drink-management-page__bulk-import-description">
              {t('management.drinks.bulkImport.description')}
            </p>
            
            <div className="drink-management-page__bulk-import-content">
              <div className="drink-management-page__file-upload-section">
                <SearchableSelect<number>
                  name="bulk_import_event"
                  label={t('management.drinks.bulkImport.selectEvent')}
                  placeholder={t('management.drinks.bulkImport.selectEventPlaceholder')}
                  clearable
                  options={events.map((event) => ({ value: event.id, label: event.name }))}
                  value={bulkImportEventId || null}
                  onChange={(value) => {
                    setBulkImportEventId(value);
                    setBulkImportResult(null);
                  }}
                  disabled={isBulkImporting}
                />

                <label className="form-field__label">
                  {t('management.drinks.bulkImport.selectFile')}
                </label>
                <input
                  type="file"
                  accept=".csv,.xlsx,.xls"
                  onChange={handleBulkImportFileChange}
                  className="drink-management-page__file-input"
                  disabled={isBulkImporting}
                />
                {bulkImportFile && (
                  <p className="drink-management-page__file-selected">
                    {t('management.drinks.bulkImport.fileSelected', { fileName: bulkImportFile.name })}
                  </p>
                )}
              </div>

              <div className="drink-management-page__bulk-import-actions">
                <Button
                  variant="ghost"
                  onClick={handleDownloadTemplate}
                  className="drink-management-page__download-btn"
                  disabled={!bulkImportEventId}
                >
                  {t('management.drinks.bulkImport.downloadTemplate')}
                </Button>
                <Button
                  variant="primary"
                  onClick={handleBulkImport}
                  disabled={!bulkImportFile || !bulkImportEventId || isBulkImporting}
                >
                  {isBulkImporting ? t('management.drinks.bulkImport.importing') : t('management.drinks.bulkImport.import')}
                </Button>
                <Button
                  variant="outline"
                  onClick={handleCancelBulkImport}
                  disabled={isBulkImporting}
                >
                  {bulkImportResult ? t('common.close') : t('common.cancel')}
                </Button>
              </div>
            </div>

            {bulkImportResult && (
              <BulkImportResult result={bulkImportResult} onDismiss={() => setBulkImportResult(null)} />
            )}
          </Card>
        )}

        {/* Drinks List */}
        <div className="drink-management-page__drinks">
          {/* Always shown, like the filters, so selecting rows doesn't push the list down */}
            <Card variant="elevated" padding="md" className={`drink-management-page__bulk-assign-bar ${selectedDrinkIds.size === 0 ? 'drink-management-page__bulk-assign-bar--idle' : ''}`}>
              <div className="drink-management-page__bulk-assign-content">
                <span className="drink-management-page__bulk-assign-count">
                  {selectedDrinkIds.size === 0
                    ? t('management.drinks.bulkAssign.hint')
                    : t('management.drinks.bulkAssign.selectedCount', {
                        count: selectedDrinkIds.size,
                        defaultValue: `${selectedDrinkIds.size} seleccionados`
                      })}
                </span>

                <SearchableSelect<number>
                  name="bulk_target_event"
                  label={t('management.drinks.bulkAssign.targetEvent', { defaultValue: 'Evento destino' })}
                  placeholder={t('management.drinks.bulkAssign.selectEvent', { defaultValue: 'Elige evento' })}
                  clearable
                  options={events.map((event) => ({ value: event.id, label: event.name }))}
                  value={bulkTargetEventId || null}
                  onChange={(value) => setBulkTargetEventId(value)}
                  disabled={isBulkAssigning || selectedDrinkIds.size === 0}
                />

                <SearchableSelect<number>
                  name="bulk_target_venue"
                  label={t('management.drinks.bulkAssign.targetVenue', { defaultValue: 'Barra destino' })}
                  placeholder={t('management.drinks.bulkAssign.noVenue', { defaultValue: '— Sin barra (Pista) —' })}
                  clearable
                  options={(bulkTargetEventId ? venues[bulkTargetEventId] || [] : []).map((venue) => ({
                    value: venue.id,
                    label: `${venue.name}${venue.venue_type_name ? ` (${venue.venue_type_name})` : ''}`,
                  }))}
                  value={bulkTargetVenueId || null}
                  onChange={(value) => setBulkTargetVenueId(value)}
                  disabled={isBulkAssigning || selectedDrinkIds.size === 0 || !bulkTargetEventId}
                />

                <Button
                  variant="primary"
                  size="sm"
                  onClick={handleBulkAssign}
                  disabled={isBulkAssigning || selectedDrinkIds.size === 0 || !bulkTargetEventId}
                >
                  {isBulkAssigning
                    ? t('common.loading')
                    : t('management.drinks.bulkAssign.assign', { defaultValue: 'Asignar' })}
                </Button>
                <Button
                  variant="error"
                  size="sm"
                  onClick={handleBulkDelete}
                  disabled={isBulkAssigning || selectedDrinkIds.size === 0}
                >
                  {t('management.drinks.bulkDelete.button')}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={clearSelection}
                  disabled={isBulkAssigning || selectedDrinkIds.size === 0}
                >
                  {t('management.drinks.bulkAssign.clear')}
                </Button>
              </div>
            </Card>
          {loading ? (
            <Card variant="elevated" padding="lg">
              <p>{t('common.loading')}</p>
            </Card>
          ) : drinks.length === 0 ? (
            <Card variant="elevated" padding="lg">
              <p>{t('management.drinks.noDrinks')}</p>
            </Card>
          ) : (
            <Card variant="elevated" padding="none" className="drink-management-page__drinks-table-card">
              <div className="drink-management-page__table-wrapper">
                <table className="drink-management-page__table">
                  <thead>
                    <tr>
                      <th className="drink-management-page__table-checkbox-col">
                        <input
                          type="checkbox"
                          checked={pageDrinks.length > 0 && pageDrinks.every((d) => selectedDrinkIds.has(d.id))}
                          onChange={(e) => {
                            // Selects or clears the rows of the current page only
                            const next = new Set(selectedDrinkIds);
                            pageDrinks.forEach((d) => (e.target.checked ? next.add(d.id) : next.delete(d.id)));
                            setSelectedDrinkIds(next);
                          }}
                        />
                      </th>
                      <th>{t('management.drinks.name')}</th>
                      <th>{t('carta.table.product')}</th>
                      <th>{t('management.drinks.type')}</th>
                      <th>{t('management.drinks.event')}</th>
                      <th>{t('management.drinks.venue', { defaultValue: 'Barra' })}</th>
                      <th>{t('management.drinks.price')}</th>
                      <th>{t('management.drinks.taxRate')}</th>
                      <th>{t('management.drinks.currentStock')}</th>
                      <th>{t('management.drinks.available')}</th>
                      <th className="drink-management-page__table-actions-col">{t('common.actions', { defaultValue: 'Acciones' })}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pageDrinks.map((drink) => (
                      <tr
                        key={drink.id}
                        className={selectedDrinkIds.has(drink.id) ? 'drink-management-page__table-row--selected' : ''}
                      >
                        <td>
                          <input
                            type="checkbox"
                            checked={selectedDrinkIds.has(drink.id)}
                            onChange={() => toggleDrinkSelection(drink.id)}
                          />
                        </td>
                        <td>
                          <div className="drink-management-page__table-name-cell">
                            <img
                              {...getProductImageProps(drink.name, 'drink', drink.image_url)}
                              alt={drink.name}
                              className="drink-management-page__table-thumb"
                            />
                            <div>
                              <div className="drink-management-page__table-name">{drink.name}</div>
                              <span className={`drink-management-page__stock-badge ${getStockStatus(drink)}`}>
                                {getStockStatusText(drink)}
                              </span>
                            </div>
                          </div>
                        </td>
                        <td>
                          {drink.product_id ? (
                            <span className="drink-management-page__product-badge drink-management-page__product-badge--linked">
                              {t('carta.table.linked')}
                            </span>
                          ) : (
                            <span
                              className="drink-management-page__product-badge drink-management-page__product-badge--unlinked"
                              title={t('carta.unlinked.text')}
                            >
                              {t('carta.table.unlinked')}
                            </span>
                          )}
                        </td>
                        <td>{drinkTypes.find(t => t.id === drink.type_id)?.name}</td>
                        <td>{events.find((event) => event.id === drink.event_id)?.name}</td>
                        <td>{getVenueName(drink.event_id, drink.event_venue_id)}</td>
                        
                        <td>{format(drink.price)}</td>
                        <td>{drink.tax_rate}%</td>
<td>
                          {drink.product_id && events.find((ev) => ev.id === drink.event_id)?.local_id ? (
                            <button
                              type="button"
                              className="carta-stock-link"
                              title={t('carta.viewStock')}
                              onClick={() => navigate(warehouseProductUrl(events.find((ev) => ev.id === drink.event_id)?.local_id!, drink.product_id!))}
                            >
                              {drink.stock_quantity} ↗
                            </button>
                          ) : (
                            drink.stock_quantity
                          )}
                        </td>
                        <td>
                            {drink.is_available ? 'Y' : 'N'}
                        </td>
                        <td>
                          <div className="drink-management-page__table-actions-cell">
                            <Button variant="primary" size="sm" onClick={() => handleEdit(drink)}>
                              {t('common.edit')}
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <Pagination
                className="drink-management-page__pagination"
                page={currentPage}
                pageSize={PAGE_SIZE}
                totalItems={drinks.length}
                onPageChange={setPage}
              />
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default DrinkManagement; 